export class Locations {

  static get FULL_HOUR_FORMAT() {
    return 'h:mmA';
  }

  static get FULL_FORMAT() {
    return 'h:mmA dddd, MMMM Do';
  }

  static get ORDERED_DAY_MAP() {
    return [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
  }

  static get easternTime() {
    return moment.tz('America/New_York');
  }

  static get localTime() {
    return moment();
  }

  static formatAddress(address) {
    return [
      [
        address.address_line1,
        address.address_line2,
      ]
        .filter(a => a !== '')
        .join('<br />'),
      [
        [
          address.locality,
          address.administrative_area,
        ]
          .filter(a => a !== '')
          .join(', '),
        address.postal_code
      ]
        .filter(a => a !== '')
        .join(' '),
    ]
      .filter(a => a !== '')
      .join('<br />');
  }

  static getScheduleTime(day, seconds) {
    return Locations.easternTime
      .day(day)
      .endOf('day')
      .add(seconds + 1, 'second');
  }

  static getScheduleLocalTime(day, seconds) {
    return Locations.getScheduleTime(day, seconds)
      .utcOffset(Locations.localTime.utcOffset());
  }

  static getNearestDayWithOpening(location, fromDay) {
    let nextDayWithOpening = fromDay;
    for (let i = 0; i < 7; i++) {
      if (location.schedule[Locations.ORDERED_DAY_MAP[nextDayWithOpening]]) {
        return nextDayWithOpening;
      }
      if (nextDayWithOpening < 6) {
        nextDayWithOpening++;
      } else {
        nextDayWithOpening = 0;
      }
    }
    return -1;
  }


  /**
   * Determines if a location is currently open.
   *
   * @param {Object} location The location to check.
   *
   * @returns {boolean} true if the location is currently open, false otherwise.
   */
  static isOpen(location) {
    return Locations.getOpenStatus(location, 'boolean');
  }


  /**
   * Gets the open status and opening schedule of a location for the current day.
   *
   * @param {Object} location The location that the schedule belongs to.
   * @returns {string} An HTML snippet for the location's opening schedule.
   */
  static renderCurrentDaySchedule(location) {
    return Locations.getOpenStatus(location, 'html');
  }

  /**
   * Cases:
   *   - Location is open on current day
   *     - and location is open 24h for current day
   *       - and next close time is at *next day* closing time
   *     - and current time is < open time
   *       - and next open time is today at open time
   *     - and current time is > close time
   *       - and next open time is on next open day at next open day's open time
   *     - and current time is between open and close time
   *       - and location closes at close time
   *   - Location is closed on current day
   *     - and next open time is on next open day at next open day's open time
   *   - Location has no schedule information
   * @param location The location.
   * @param mode Specifies the nature of the return value.
   *  - 'html': An HTML snippet showing if the locaton is open or closed, with additional information.
   *  - 'status': true if open, false if closed
   * @returns {string}
   */
  static getOpenStatus(location, mode) {
    const template = vars => `<div class="current">${vars.open}<span class="message">${vars.message}</span><button>Click to see full schedule</button></div>`;
    const nowInEasternTime = Locations.easternTime;
    const nowInLocalTime = Locations.localTime;
    const currentDay = nowInEasternTime.day();
    // If mode is not provided, return an HTML snippet.
    if (typeof mode == 'undefined') {
      var mode = 'html';
    }

    let nextOpenDay = Locations.getNearestDayWithOpening(location, currentDay);
    if (nextOpenDay === -1) {
      if (mode == 'boolean') {
        return false;
      }
      return template({
        open: '<span class="closed">Closed</span>',
        message: `Schedule is unknown.`,
      });
    }
    if (nextOpenDay !== currentDay) {
      const nextOpen = nowInEasternTime
        .clone()
        .startOf('day')
        .add(nextOpenDay < currentDay ? 7 - currentDay + nextOpenDay : nextOpenDay - currentDay, 'day')
        .add(location.schedule[Locations.ORDERED_DAY_MAP[nextOpenDay]].from, 'second')
        .utcOffset(nowInLocalTime.utcOffset());
      if (mode == 'boolean') {
        return false;
      }
      return template({
        open: '<span class="closed">Closed</span>',
        message: `Opens ${nextOpen.format(Locations.FULL_FORMAT)}.`,
      });
    } else {
      const scheduleForDay = location.schedule[Locations.ORDERED_DAY_MAP[nextOpenDay]];
      if (scheduleForDay.from === scheduleForDay.to) {
        if (mode == 'boolean') {
          return true;
        }
        return template({
          open: '<span class="open">Open</span>',
          message: `24 Hours`,
        });
      }
      const open = nowInEasternTime
        .clone()
        .startOf('day')
        .add(scheduleForDay.from, 'second');
      const close = nowInEasternTime
        .clone()
        .startOf('day')
        .add(scheduleForDay.to, 'second');
      if (nowInEasternTime.isBetween(open, close)) {
        const closesAt = close
          .clone()
          .utcOffset(nowInLocalTime.utcOffset());
        if (mode == 'boolean') {
          return true;
        }
        return template({
          open: '<span class="open">Open</span>',
          message: `Closes ${Locations.formatTime(closesAt)}`,
        });
      } else if (nowInEasternTime.isBefore(open)) {
        const openAt = open
          .clone()
          .utcOffset(nowInLocalTime.utcOffset());
        if (mode == 'boolean') {
          return false;
        }
        return template({
          open: '<span class="closed">Closed</span>',
          message: `Opens ${Locations.formatTime(openAt)}`,
        });
      } else if (nowInEasternTime.isAfter(close)) {
        nextOpenDay = Locations.getNearestDayWithOpening(location, nextOpenDay < 6 ? nextOpenDay + 1 : 0);
        const nextOpen = nowInEasternTime
          .clone()
          .startOf('day')
          .add(nextOpenDay < currentDay ? 7 - currentDay + nextOpenDay : nextOpenDay - currentDay, 'day')
          .add(location.schedule[Locations.ORDERED_DAY_MAP[nextOpenDay]].from, 'second')
          .utcOffset(nowInLocalTime.utcOffset());
        if (mode == 'boolean') {
          return false;
        }
        return template({
          open: '<span class="closed">Closed</span>',
          message: `Opens ${nextOpen.format(Locations.FULL_FORMAT)}`,
        });
      } else {
        throw 'Shouldn\'t get here.';
      }
    }
  }

  static renderDailySchedule(location) {
    const currentDayOfWeek = moment().day();
    return Locations
      .ORDERED_DAY_MAP
      .map(day => {
        const schedule = location.schedule[day];
        const weekday = moment().day(day);
        const dayOfWeek = weekday.format('dddd');
        const components = [];
        const classes = [
          'day-time',
        ];
        if (schedule) {
          if (schedule.from === schedule.to) {
            components.push(`<span class="time">Open 24 Hours</span>`);
          } else {
            const open = Locations.getScheduleLocalTime(day, schedule.from);
            const close = Locations.getScheduleLocalTime(day, schedule.to);
            components.push(`<span class="time">${Locations.formatTime(open)} - ${Locations.formatTime(close)}</span>`);
          }
        } else {
          components.push('<span class="closed">Closed</span>');
        }
        if (currentDayOfWeek === weekday.day()) {
          classes.push('current-day');
        }
        return `<div class="${classes.join(' ')}"><span class="day">${dayOfWeek}</span>${components.join()}</div>`;
      })
      .join('');
  }

  static formatTime(time) {
    let meridiem = 'a.m.';
    if (time.hours() >= 12) {
      meridiem = 'p.m.';
    }
    let format = 'h';
    if (time.minutes() > 0) {
      format = 'h:mm';
    }
    return `${time.format(format)} ${meridiem}`;
  }

  static renderCount(locations) {
    return `<div>Showing <span>${locations.length}</span> ${Drupal.formatPlural(locations.length, 'Result', 'Results')}</div>`
  }

}
